import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button, Modal, PageContainer } from '@react-apps/design-system';
import type { ContentItem } from '../../domain/entities/content.entity';
import {
  useBulkDeleteQuestionsRunner,
  useExamPreview,
  useInvalidateExams,
  useMoveExamQuestionsRunner,
  useSetQuestionAnswerKey,
} from '../hooks/use-exams';
import { useInstitutionsById } from '../hooks/use-institutions';
import { useArchiveContentItem } from '../hooks/use-content-items';
import { useRequestQueue } from '../hooks/use-request-queue';
import { useExamQuestionMove } from '../hooks/use-exam-question-move';
import { QuestionSpotlightProvider } from '../hooks/use-question-spotlight';
import { useCanWriteExams } from '../hooks/use-content-permissions';
import {
  formatInstitution,
  formatInstitutionLabel,
  readExamBody,
} from '../exam-body';
import {
  ContentStatusBadge,
  contentTypeLabel,
  EXAM_TYPE_LABELS,
  ErrorState,
  Field,
  Mono,
  SectionCard,
  formatDateTime,
  getApiErrorMessage,
  notifyDone,
  notifyFailed,
} from '../display';
import { AnswerKeyTable } from '../components/AnswerKeyTable';
import { QuestionPreviewCard } from '../components/QuestionPreviewCard';
import { QuestionFormModal } from '../components/QuestionFormModal';
import { ExamPickerModal } from '../components/ExamPickerModal';
import { BulkActionsBar } from '../components/BulkActionsBar';
import { ConfirmModal } from '../components/ConfirmModal';
import { RequestQueuePanel } from '../components/RequestQueuePanel';
import { DraftExamExitGuard } from '../components/DraftExamExitGuard';

/** Mesmo teto do import — é a mesma fila, com o mesmo motivo. */
const BULK_CONCURRENCY = 3;

/**
 * Preview/gestão de uma prova. Uma tela só para `DRAFT`, `PUBLISHED` e
 * `ARCHIVED` — o `status` que vem no payload decide quais botões aparecem, não
 * há tela separada por status. Em `DRAFT`, publicar e descartar moram no
 * `DraftExamExitGuard`, que também segura a saída da tela.
 *
 * A fila de requisições fica montada durante toda a vida da página: toda ação
 * em massa (excluir, transferir) passa por ela, então o progresso e o retry por
 * item são visíveis sem depender de qual ação foi disparada.
 *
 * A prova não edita a questão: cada card leva à ficha dela ("Abrir
 * questão"). O que se faz aqui age sobre a prova — a ordem, a seleção, remover
 * e excluir — e o gabarito, que corrige a letra célula a célula sob o lock de
 * cada questão.
 *
 * Dois estados vivem aqui, e não nos cards, porque atravessam cards: **o lock
 * da ordem da prova** (`useExamQuestionMove` — um lock do `examId` para todos
 * os "Mover") e **o alvo do "rolar até e piscar"** (`QuestionSpotlightProvider`
 * — o gabarito aponta para os cards, e os cards apontam de volta para o
 * gabarito).
 */
export function ExamPreviewPage() {
  const { examId } = useParams<{ examId: string }>();
  const location = useLocation();
  const { data, isLoading, isError, error } = useExamPreview(examId);
  const { data: institutionsById } = useInstitutionsById();
  const archive = useArchiveContentItem();
  const canWriteExams = useCanWriteExams();

  const queue = useRequestQueue(BULK_CONCURRENCY);
  const deleteQuestions = useBulkDeleteQuestionsRunner();
  const moveQuestions = useMoveExamQuestionsRunner();
  const invalidateExams = useInvalidateExams();
  const move = useExamQuestionMove(examId);
  const answerKey = useSetQuestionAnswerKey(examId ?? '');

  /**
   * Gravar o gabarito de uma questão pelo seletor da grade. O aviso vem de quem
   * chama porque só lá se sabe o que mudou ("Gabarito da questão 23: C" diz
   * mais que "salvo").
   *
   * O card **não** passa mais por aqui: anular virou interruptor nas Marcações
   * do form, salvo junto com o resto pelo botão do card.
   */
  const setAnswer = (
    input: {
      questionId: string;
      correctAlternativeId?: string;
      annulled?: boolean;
    },
    done: string,
  ) =>
    answerKey.mutate(input, {
      onSuccess: () => notifyDone(done),
      onError: (err) => notifyFailed(err, 'Falha ao salvar o gabarito'),
    });

  /**
   * Quantas questões acabaram de ser importadas, quando se chega aqui vindo da
   * tela de importação. Fecha o ciclo do outro lado da navegação: a tela nova é
   * consequência do que a pessoa acabou de fazer, não algo que abriu sozinho.
   */
  const [importedQuestions, setImportedQuestions] = useState<number | null>(
    () =>
      (location.state as { importedQuestions?: number } | null)
        ?.importedQuestions ?? null,
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  /** Destino escolhido esperando a decisão rascunho×publicar. */
  const [pendingTarget, setPendingTarget] = useState<ContentItem | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  // Ids de job precisam ser únicos entre lotes (a fila acumula o histórico da
  // sessão), e o contador serve também de rótulo estável para retry.
  const batchSeqRef = useRef(0);

  // Lido uma vez: sem limpar o histórico, um F5 três dias depois reanunciaria
  // uma importação que já é passado.
  useEffect(() => {
    if (importedQuestions !== null) {
      window.history.replaceState({}, '');
    }
  }, [importedQuestions]);

  const toggleSelected = useCallback((questionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  }, []);

  // Lote terminou: o preview em cache está velho e a seleção já não descreve
  // nada (as questões saíram da prova ou deixaram de existir).
  useEffect(() => {
    if (!batchRunning || queue.isRunning) return;
    setBatchRunning(false);
    setSelectedIds(new Set());
    invalidateExams();
  }, [batchRunning, queue.isRunning, invalidateExams]);

  if (isLoading) {
    return (
      <PageContainer maxWidth="5xl" spacing="none">
        <p className="text-(--color-text-secondary) text-sm">Carregando…</p>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer maxWidth="5xl" spacing="sm">
        <BackLink />
        <ErrorState
          message={getApiErrorMessage(error, 'Prova não encontrada.')}
        />
      </PageContainer>
    );
  }

  const { exam } = data;
  // Ordena no cliente: é isso que faz o card pular de lugar assim que a
  // posição muda, sem depender da ordem em que o payload veio.
  const questions = [...data.questions].sort((a, b) => a.position - b.position);
  const body = readExamBody(exam);
  // Simulado não é dono das questões, só aponta: criar, excluir e transferir
  // agem sobre a questão na prova dona e saem desta tela; o card oferece
  // "remover do simulado". Ver primum-monolith
  // `docs/reference/modules/content.md` § Dono vs. apontador.
  const isMockExam = body.examKind === 'MOCK_EXAM';

  // Derivado da lista atual, não do `Set` cru: id que sobrou selecionado depois
  // de a questão sair da prova simplesmente não conta.
  const selected = questions.filter((entry) =>
    selectedIds.has(entry.question.id),
  );
  const hasDraftSelected = selected.some(
    (entry) => entry.question.status === 'DRAFT',
  );

  const startBulkDelete = () => {
    setIsBulkDeleteOpen(false);
    setBatchRunning(true);
    const seq = batchSeqRef.current++;
    queue.enqueue(
      selected.map((entry) => ({
        id: `delete-${entry.question.id}-${seq}`,
        label: `Excluir questão ${entry.position}`,
        run: () => deleteQuestions([entry.question.id]),
      })),
    );
  };

  const startMove = (
    target: ContentItem,
    publishMode?: 'KEEP_DRAFT' | 'PUBLISH',
  ) => {
    const questionIds = selected.map((entry) => entry.question.id);
    const targetBody = readExamBody(target);
    const targetLabel =
      `${formatInstitution(targetBody.institutionId, institutionsById)} ${targetBody.year ?? ''}`.trim();
    setBatchRunning(true);
    queue.enqueue([
      {
        id: `move-${target.id}-${batchSeqRef.current++}`,
        label: `Transferir ${questionIds.length} ${questionIds.length === 1 ? 'questão' : 'questões'} → ${targetLabel}`,
        run: () =>
          moveQuestions({ questionIds, targetExamId: target.id, publishMode }),
      },
    ]);
  };

  const handlePick = (target: ContentItem) => {
    setIsPickerOpen(false);
    // A pergunta "manter rascunho ou publicar?" só existe quando ela tem
    // consequência: questão em rascunho entrando numa prova já publicada.
    if (hasDraftSelected && target.status === 'PUBLISHED') {
      setPendingTarget(target);
      return;
    }
    startMove(target);
  };

  return (
    <QuestionSpotlightProvider>
      <PageContainer maxWidth="5xl">
        <BackLink />

        {importedQuestions !== null && (
          <div className="flex items-center gap-3 rounded-lg border border-green-300 bg-green-50 px-4 py-3 dark:border-green-500/40 dark:bg-green-500/5">
            <span
              aria-hidden
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-600 text-sm text-white"
            >
              ✓
            </span>
            <p className="text-(--color-text-primary) flex-1 text-sm">
              Prova importada — {importedQuestions}{' '}
              {importedQuestions === 1 ? 'questão' : 'questões'}, em{' '}
              <strong>rascunho</strong>. Confira as questões abaixo.
            </p>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setImportedQuestions(null)}
            >
              Ok
            </Button>
          </div>
        )}

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <ContentStatusBadge status={exam.status} />
              {exam.deletedAt && (
                <span className="text-xs font-medium text-red-500">
                  Excluída
                </span>
              )}
            </div>
            <h1 className="text-(--color-text-primary) text-xl font-semibold">
              {formatInstitution(body.institutionId, institutionsById)}{' '}
              {body.year ?? ''} — {body.variant || 'sem variante'}
            </h1>
            <Mono>{exam.id}</Mono>
          </div>

          <div className="flex flex-wrap gap-2">
            {/* Arquivar prova é do `systemAdministrator` (`useCanWriteExams`).
                Publicar a prova em rascunho é o "Publicar tudo" do aviso fixo
                (`DraftExamExitGuard`). */}
            {exam.status === 'PUBLISHED' && canWriteExams && (
              <Button
                variant="secondary"
                disabled={archive.isPending}
                onClick={() =>
                  archive.mutate(exam.id, {
                    onSuccess: () => notifyDone('Prova arquivada'),
                    onError: (err) =>
                      notifyFailed(err, 'Falha ao arquivar a prova'),
                  })
                }
              >
                {archive.isPending ? 'Arquivando…' : 'Arquivar'}
              </Button>
            )}
          </div>
        </div>

        <DraftExamExitGuard
          examId={exam.id}
          active={exam.status === 'DRAFT' && !exam.deletedAt}
          questionIds={questions.map((entry) => entry.question.id)}
          isMockExam={isMockExam}
        />

        <SectionCard title={contentTypeLabel('EXAM', body.examKind)}>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Field
              label="Instituição"
              value={formatInstitutionLabel(
                body.institutionId,
                institutionsById,
              )}
            />
            <Field label="Ano" value={body.year} />
            <Field label="Variante" value={body.variant} />
            {/* Real × simulado não é campo: o título do card já diz
                "Simulado" (`contentTypeLabel`). */}
            <Field
              label="Tipo da prova"
              value={body.examType ? EXAM_TYPE_LABELS[body.examType] : null}
            />
            <Field label="Criada em" value={formatDateTime(exam.createdAt)} />
          </div>
        </SectionCard>

        <RequestQueuePanel
          title="Fila de ações em massa"
          items={queue.items}
          counts={queue.counts}
          onRetry={queue.retry}
        />

        <AnswerKeyTable
          questions={questions}
          saving={answerKey.isPending}
          onSetAnswer={setAnswer}
        />

        <div className="space-y-3">
          <h2 className="text-(--color-text-primary) text-sm font-semibold">
            Questões ({questions.length})
          </h2>
          {questions.length === 0 ? (
            <ErrorState
              message={
                isMockExam
                  ? 'Este simulado ainda não aponta para nenhuma questão. Para incluir uma, abra a ficha do simulado e use "Relacionar" com a relação "Aponta para".'
                  : 'Esta prova ainda não tem questões.'
              }
            />
          ) : (
            questions.map((entry) => (
              <QuestionPreviewCard
                key={entry.question.id}
                entry={entry}
                examId={exam.id}
                selected={selectedIds.has(entry.question.id)}
                onToggleSelected={toggleSelected}
                move={move}
                isMockExam={isMockExam}
              />
            ))
          )}
        </div>

        {/* FAB: `fixed` acompanha o scroll por natureza — sem listener. O
            botão é o do design-system; só o posicionamento vem de fora.
            Mantém o rótulo escrito: é ação única, não repetida numa lista. */}
        {!isMockExam && (
          <div className="fixed bottom-6 right-6 z-40">
            <Button type="button" onClick={() => setIsCreateOpen(true)}>
              + Criar nova questão
            </Button>
          </div>
        )}
        {/* Sem `position`: quem decide o fim da prova é o backend, dentro da
            transação. Mandar uma posição calculada deste payload (que pode
            estar velho) cairia no guard de retry idempotente do
            `CreateExamQuestionUseCase` e devolveria a questão que já ocupa a
            posição, em vez de criar a nova. */}
        <QuestionFormModal
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          examId={exam.id}
        />

        <BulkActionsBar
          count={isMockExam ? 0 : selected.length}
          onDelete={() => setIsBulkDeleteOpen(true)}
          onTransfer={() => setIsPickerOpen(true)}
          onClear={() => setSelectedIds(new Set())}
        />

        <ConfirmModal
          open={isBulkDeleteOpen}
          onClose={() => setIsBulkDeleteOpen(false)}
          onConfirm={startBulkDelete}
          title={`Excluir ${selected.length} ${selected.length === 1 ? 'questão' : 'questões'}?`}
          description="As que estiverem em rascunho são removidas de vez; as publicadas saem da prova mas o registro é preservado (soft delete). Uma linha por questão aparece na fila."
          confirmLabel="Excluir"
        />

        <ExamPickerModal
          open={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
          excludeExamId={exam.id}
          onPick={handlePick}
        />

        <PublishModeModal
          target={pendingTarget}
          count={selected.length}
          onClose={() => setPendingTarget(null)}
          onChoose={(publishMode) => {
            const target = pendingTarget;
            setPendingTarget(null);
            if (target) startMove(target, publishMode);
          }}
        />
      </PageContainer>
    </QuestionSpotlightProvider>
  );
}

/**
 * Rascunho indo para prova publicada: a questão continua rascunho (invisível
 * para o aluno na prova de destino) ou é publicada junto? O backend não escolhe
 * por conta própria — recebe `publishMode` já decidido.
 */
function PublishModeModal({
  target,
  count,
  onClose,
  onChoose,
}: {
  target: ContentItem | null;
  count: number;
  onClose: () => void;
  onChoose: (publishMode: 'KEEP_DRAFT' | 'PUBLISH') => void;
}) {
  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title="Publicar as questões em rascunho?"
      description={`A prova de destino está publicada, e há questão em rascunho entre as ${count} selecionadas.`}
    >
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => onChoose('KEEP_DRAFT')}
        >
          Manter como rascunho
        </Button>
        <Button type="button" onClick={() => onChoose('PUBLISH')}>
          Publicar junto
        </Button>
      </div>
    </Modal>
  );
}

function BackLink() {
  return (
    <Link
      to="/content/exams"
      className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-sm"
    >
      ← Voltar para Provas
    </Link>
  );
}
